import { ActionTypes } from '../ChannelArchitecture.d';
import { reconstructBlockName, shouldDisablePlacement } from '../utils/architecture.utils';

const buildProgrammesByBlockName = (programmes) => {
  const programmesMap = new Map();
  programmes.forEach((programme) => {
    const blockKey = reconstructBlockName(programme.blockName);
    if (!programmesMap.has(blockKey)) programmesMap.set(blockKey, programme);
  });
  return programmesMap;
};

const getBlockReferenceData = (programme, programmesByBlockName) => {
  if (!programme.blockReference) return null;
  const reference = programmesByBlockName.get(reconstructBlockName(programme.blockReference));
  if (!reference) return null;

  return {
    titleGroup: reference.titleGroupName ?? null,
    series: reference.seriesName ?? null,
    type: reference.type ?? null,
    blockName: reference.blockName ?? null,
  };
};

// Resolve block reference data for each programme based on its block reference
const attachBlockReferenceData = (programmes) => {
  const programmesByBlockName = buildProgrammesByBlockName(programmes);
  return programmes.map((programme) => ({
    ...programme,
    blockReferenceData: getBlockReferenceData(programme, programmesByBlockName),
  }));
};

const baseSchedulesReducer = (state, { actionType, payload, saveChange }) => {
  switch (actionType) {
    case ActionTypes.CREATE:
      return {
        ...state,
        previous:
          state.previous.length == 0
            ? [{ item: [...state.programmes] }]
            : [...state.previous, { item: [...state.programmes] }],
        programmes: [...state.programmes, ...payload.item],
      };

    case ActionTypes.UPDATE: {
      const itemsById = new Map(payload.items.map((item) => [item.id, item]));
      const updated = state.programmes.map((item) => {
        return itemsById.has(item.id) ? { ...itemsById.get(item.id) } : item;
      });
      return {
        ...state,
        previous: [...state.previous, { item: [...state.programmes] }],
        programmes: updated,
      };
    }

    case ActionTypes.DELETE: {
      const { idsToDelete, itemsToUpdate } = payload;
      const itemsById = new Map(itemsToUpdate.map((item) => [item.id, item]));

      const programmes = state.programmes
        .filter((item) => !idsToDelete.includes(item.id))
        .map((item) => (itemsById.has(item.id) ? { ...itemsById.get(item.id) } : item));

      return {
        ...state,
        previous: [...state.previous, { item: [...state.programmes] }],
        programmes,
      };
    }

    case ActionTypes.UPDATEALL:
      return {
        previous: !state.previous ? [] : saveChange ? [...state.previous, { item: [...state.programmes], action: 'UPDATEALL' }] : [...state.previous],
        programmes: payload.programmes,
      };
    case ActionTypes.UNDO:
      return {
        ...state,
        programmes: payload?.previous ? state.programmes : state.previous[state.previous.length - 1].item,
        previous: payload?.previous ? payload.previous : state.previous.slice(0, -1),
      };

    case ActionTypes.CLEARHISTORY:
      return { ...state, previous: [] };

    default:
      return state;
  }
};

export function schedulesReducer(state, action) {
  const nextState = baseSchedulesReducer(state, action);
  if (nextState === state) return state;
  return { ...nextState, programmes: attachBlockReferenceData(nextState.programmes) };
}

const buildCAModalSharedFields = (state, payload, type, disablePlacement) => ({
  type,
  disablePlacement,
  disableMaxCount: false,
  disableLayout: false,
  disableSequential: false,
  disableStartTime: false,
  disableLinkReference: state.disableLinkToggle,
  disableType: false,
  disableReccurrence: false,
  disableEndTime: false,
  disableLinkToggle: false,
  layout: payload.layout !== undefined ? payload.layout : state.layout,
});

const getDefaultTypeOverride = () => ({
  disableSeason: true,
  disableSeries: true,
  disableTitleGroup: true,
  disableDayOfWeek: false,
  sequential: false,
  series: null,
  seasons: null,
  titleGroup: null,
});

const getSeriesTypeOverride = (state) => ({
  disableSeason: false,
  disableSeries: false,
  disableTitleGroup: true,
  disableDayOfWeek: Boolean(state.series),
  titleGroup: null,
});

const getTitleGroupTypeOverride = (state) => ({
  disableSeason: true,
  disableSeries: true,
  disableTitleGroup: false,
  disableDayOfWeek: Boolean(state.titleGroup),
  series: null,
  seasons: null,
});

const getRepeatTypeOverride = (state) => ({
  disableSeason: !state.seasons?.id,
  disableSeries: !state.series?.id,
  disableTitleGroup: !(state.titleGroup || state.titleGroupID),
  disableDayOfWeek: false,
});

const cAModalTypeOverrides = new Map([
  ['Series', getSeriesTypeOverride],
  ['TitleGroup', getTitleGroupTypeOverride],
  ['Title Group', getTitleGroupTypeOverride],
  ['Repeat', getRepeatTypeOverride],
]);

export const cAModalReducer = (state, payload) => {
  const { action, newValues, type } = payload;

  if (action === 'changeValue') {
    return {
      ...state,
      ...newValues,
    };
  }

  const disablePlacement = payload.disablePlacement ?? shouldDisablePlacement(type);
  const getTypeOverride = cAModalTypeOverrides.get(type) ?? getDefaultTypeOverride;

  return {
    ...state,
    ...buildCAModalSharedFields(state, payload, type, disablePlacement),
    ...getTypeOverride(state),
  };
};
